/**
 * L'atelier de reconstruction — un établi, pas une visionneuse.
 *
 * Le serveur déroule le rejeu d'un relevé (SSE) ; l'atelier le **rythme** et
 * le donne à comprendre : ce qu'il est en train de faire (le journal de
 * bord), ce qui est déjà recouvré (la scène), ce qui est sûr et ce qui est
 * deviné (la hiérarchie de nommage du relevé lui-même), ce qui reste douteux
 * (incertitudes et avertissements non levés), et d'où vient chaque polygone
 * (fichier, calque, étiquettes, décisions du métreur).
 *
 * **L'état est une machine, pas un booléen.** Ouverture, lecture, cadrage,
 * construction (pas n sur N), recoupement, bilan, terminé, échec : chaque
 * phase se voit dans le bandeau, et le rythme — progressif, direct, pause —
 * est un choix de lecture orthogonal à la phase, jamais un état du serveur.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { api, ErreurApi } from "../../api/client";
import { Chiffrage } from "./Chiffrage";

interface Entete {
  fichier: string;
  source: string;
  strategie: string;
  unite: string;
  unite_source: string;
  locaux: number;
  surface_declaree_m2: number | null;
  avertissements_entete: string[];
}

interface Cadre {
  bornes: [number, number, number, number];
  emprise_m: [number, number];
  dessinables: number;
}

interface Averti {
  texte: string;
  leve: boolean;
}

interface PieceDEnsemble {
  nom: string | null;
  aire_m2: number | null;
  perimetre_m?: number | null;
  cloison_manquante: boolean;
  wkt: string;
}

interface LocalReconstruit {
  rang: number;
  pas: number;
  sur: number;
  nom: string;
  origine_du_nom: string;
  nommage: "deterministe" | "inference" | "anonyme";
  noms_dedans: string[];
  multi: boolean;
  calque: string;
  source: string;
  wkt: string;
  aire_m2: number;
  aire_declaree_m2: number | null;
  aire_recalculee_m2: number | null;
  aire_verifiee: boolean;
  perimetre_m: number | null;
  pieces?: PieceDEnsemble[];
  cloisons_m2?: number | null;
  sous_zones?: { materiau: string; aire_m2: number | null;
                 etiquette: string | null }[];
  hauteurs_etiquetees?: { genre: string; valeur_m: number | null }[];
  incertitudes: string[];
  avertissements: Averti[];
  decisions: { question: string; reponse: string; origine: string }[];
}

interface Chevauchement { a: string; b: string; aire_m2: number }

interface Bilan {
  surface_relevee_m2: number;
  surface_declaree_m2: number | null;
  ecart_m2: number | null;
  comptes: Record<string, number>;
  avertissements_non_leves: number;
  decisions_connues: number;
}

type Phase =
  | { nom: "ouverture" }
  | { nom: "lecture" }
  | { nom: "cadrage" }
  | { nom: "construction"; pas: number; sur: number }
  | { nom: "recoupement" }
  | { nom: "bilan" }
  | { nom: "termine" }
  | { nom: "echec"; motif: string };

type Rythme = "progressif" | "pause" | "direct";

const PHASES: { clef: Phase["nom"]; libelle: string }[] = [
  { clef: "lecture", libelle: "Lecture" },
  { clef: "cadrage", libelle: "Cadrage" },
  { clef: "construction", libelle: "Construction" },
  { clef: "recoupement", libelle: "Recoupement" },
  { clef: "bilan", libelle: "Bilan" },
];

const RANG_DES_PHASES: Record<string, number> = {
  ouverture: -1, lecture: 0, cadrage: 1, construction: 2,
  recoupement: 3, bilan: 4, termine: 5, echec: 5,
};

/** Les anneaux d'un WKT POLYGON/MULTIPOLYGON, en couples [x, y]. */
function anneaux(wkt: string): number[][][] {
  const resultats: number[][][] = [];
  for (const [, corps] of wkt.matchAll(/\(([^()]+)\)/g)) {
    const anneau: number[][] = [];
    for (const point of corps!.split(",")) {
      const [x, y] = point.trim().split(/\s+/).map(Number);
      if (Number.isFinite(x) && Number.isFinite(y)) anneau.push([x!, y!]);
    }
    if (anneau.length >= 3) resultats.push(anneau);
  }
  return resultats;
}

/** Le chemin SVG d'un local — l'axe Y du plan monte, celui du SVG descend. */
function chemin(rings: number[][][]): string {
  return rings.map((anneau) =>
    "M" + anneau.map(([x, y]) => `${x},${-y!}`).join(" L") + " Z").join(" ");
}

function centre(rings: number[][][]): [number, number] {
  const points = rings[0] ?? [];
  const sx = points.reduce((somme, [x]) => somme + x!, 0);
  const sy = points.reduce((somme, [, y]) => somme + y!, 0);
  return points.length ? [sx / points.length, -sy / points.length] : [0, 0];
}

function surfaceTexte(valeur: number | null | undefined): string {
  return valeur == null ? "—"
    : `${valeur.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} m²`;
}

/** Ce qu'une annotation sait porter — le miroir du canon serveur. */
const GENRES_ANNOTATION = [
  { clef: "nom", libelle: "Nom du local", unite: null },
  { clef: "etiquette", libelle: "Étiquette", unite: null },
  { clef: "commentaire", libelle: "Commentaire", unite: null },
  { clef: "surface", libelle: "Surface retenue", unite: "m²" },
  { clef: "hsp", libelle: "Hauteur sous plafond", unite: "m" },
  { clef: "hsfp", libelle: "Hauteur sous faux plafond", unite: "m" },
] as const;

/** Comment le journal désigne un local : par son nom, ou par son aire quand
 *  il n'en a pas — au centime près, comme les signalements l'écrivent. */
function designation(local: LocalReconstruit): string {
  return local.nom || `face de ${local.aire_m2.toFixed(2).replace(".", ",")} m²`;
}

/** Aire/périmètre à CHIFFRER : un contour inféré (ou une surface écrite seule) se
 *  chiffre sur l'aire ÉCRITE, autorité, et NE livre PAS son périmètre calculé —
 *  géométrie non validée. Même garde que l'onglet Reconstruction (« jamais un
 *  nombre en silence », étendu à la longueur). */
function metreChiffrage(l: {
  source?: string; aire_m2?: number | null; aire_declaree_m2?: number | null;
  perimetre_m?: number | null;
}): { aire: number | null; perimetre: number | null } {
  if (l.source === "contour inféré" || l.source === "surface écrite seule") {
    return { aire: l.aire_declaree_m2 ?? l.aire_m2 ?? null, perimetre: null };
  }
  return { aire: l.aire_m2 ?? null, perimetre: l.perimetre_m ?? null };
}

const LIBELLES_NOMMAGE = {
  deterministe: "Déterministe — étiquette lue dans le contour",
  inference: "Inférence — étiquette voisine rapprochée",
  anonyme: "Anonyme — aucune étiquette rattachable",
} as const;

/** Comment le registre désigne une pièce d'ensemble : son nom, ou son aire
 *  au centime près — le miroir exact de `designation_de_piece` côté serveur,
 *  et de la décision « face de 25,27 m² » posée avant ce mode. */
function designationDePiece(piece: PieceDEnsemble): string {
  if (piece.nom?.trim()) return piece.nom.trim();
  return `face de ${(piece.aire_m2 ?? 0).toFixed(2).replace(".", ",")} m²`;
}

/** Le canon serveur de l'annotation « nom » — pour retrouver au registre
 *  les pièces déjà nommées par le métreur. */
const QUESTION_DU_NOM = "nom du local retenu par le métreur";

/** Le mode nommage est une machine, pas des booléens : chaque étape dit ce
 *  que l'écran attend — la liste, un choix de chemin, la main, le conseil
 *  de l'agent, la proposition à valider, l'envoi. */
type EtapeNommage =
  | { nom: "liste" }
  | { nom: "choix"; piece: number }
  | { nom: "manuel"; piece: number; valeur: string }
  | { nom: "conseil"; piece: number }
  | { nom: "proposition"; piece: number; valeur: string; justification: string;
      sources: { origine: string; texte: string }[]; deja: string | null }
  | { nom: "envoi"; piece: number; valeur: string };

export function Atelier({
  affaireId, chemin: cheminReleve, nom, mode, fermer,
}: {
  affaireId: string;
  chemin: string;
  nom: string;
  mode: "progressif" | "direct";
  fermer: () => void;
}) {
  const [phase, poserPhase] = useState<Phase>({ nom: "ouverture" });
  const [rythme, poserRythme] = useState<Rythme>(mode);
  const [entete, poserEntete] = useState<Entete | null>(null);
  const [cadre, poserCadre] = useState<Cadre | null>(null);
  const [locaux, poserLocaux] = useState<LocalReconstruit[]>([]);
  const [chevauchements, poserChevauchements] = useState<Chevauchement[]>([]);
  const [bilan, poserBilan] = useState<Bilan | null>(null);
  const [bord, poserBord] = useState<string[]>([]);
  const [survole, poserSurvole] = useState<number | null>(null);
  const [epingle, poserEpingle] = useState<number | null>(null);
  const [formulaire, poserFormulaire] = useState<{
    genre: string; valeur: string;
  } | null>(null);
  const [erreurAnnotation, poserErreurAnnotation] = useState("");
  const [envoiAnnotation, poserEnvoiAnnotation] = useState(false);
  // Un chiffrement se lance sur un ensemble entier OU sur une de ses
  // pièces : l'état ne retient que ce que la mission demande — le nom
  // effectif (validé au registre s'il y en a un), l'aire, le périmètre.
  const [chiffrage, poserChiffrage] = useState<{
    nom: string; aire: number | null; perimetre: number | null;
  } | null>(null);
  const [nommage, poserNommage] = useState<EtapeNommage | null>(null);
  const [pieceVive, poserPieceVive] = useState<number | null>(null);
  const [nomsPieces, poserNomsPieces] = useState<Record<string, string>>({});
  const [erreurNommage, poserErreurNommage] = useState("");

  const file = useRef<Record<string, unknown>[]>([]);
  const finVue = useRef(false);
  const bordRef = useRef<HTMLDivElement>(null);
  const planRef = useRef<SVGSVGElement>(null);

  const noter = (ligne: string) =>
    poserBord((avant) => [...avant.slice(-120), ligne]);

  function appliquer(evenement: Record<string, unknown>) {
    const genre = evenement.genre as string;
    if (genre === "lecture") {
      const lu = evenement as unknown as Entete;
      poserEntete(lu);
      poserPhase({ nom: "lecture" });
      noter(`Relevé lu : ${lu.locaux} contour(s), stratégie « ${lu.strategie} »,`
        + ` unités ${lu.unite} (${lu.unite_source}).`);
      for (const avertissement of lu.avertissements_entete) {
        noter(`⚠ En tête du relevé : ${avertissement}`);
      }
    } else if (genre === "cadrage") {
      const vu = evenement as unknown as Cadre;
      poserCadre(vu);
      poserPhase({ nom: "cadrage" });
      noter(`Emprise cadrée : ${vu.emprise_m[0]} × ${vu.emprise_m[1]} m — `
        + `${vu.dessinables} contour(s) dessinable(s).`);
    } else if (genre === "local") {
      const local = evenement as unknown as LocalReconstruit;
      poserLocaux((avant) => [...avant, local]);
      poserPhase({ nom: "construction", pas: local.pas, sur: local.sur });
      noter(`${local.pas}/${local.sur} — ${local.nom || "(sans nom)"} : `
        + `${surfaceTexte(local.aire_m2)}, ${local.nommage}`
        + `${local.aire_verifiee ? ", géométrie vérifiée" : ""}`
        + `${local.incertitudes.length
          ? ` — ${local.incertitudes.length} incertitude(s)` : ""}`);
    } else if (genre === "recoupement") {
      const trouves = (evenement.chevauchements ?? []) as Chevauchement[];
      poserChevauchements(trouves);
      poserPhase({ nom: "recoupement" });
      noter(trouves.length
        ? `Recoupement : ${trouves.length} chevauchement(s) mesurés.`
        : "Recoupement : aucun chevauchement au-delà du bruit d'accrochage.");
      for (const c of trouves) {
        noter(`⚠ « ${c.a} » et « ${c.b} » se disputent ${c.aire_m2} m².`);
      }
    } else if (genre === "bilan") {
      const fait = evenement as unknown as Bilan;
      poserBilan(fait);
      poserPhase({ nom: "bilan" });
      noter(`Bilan : ${surfaceTexte(fait.surface_relevee_m2)} relevés`
        + (fait.surface_declaree_m2
          ? ` pour ${surfaceTexte(fait.surface_declaree_m2)} écrits au plan`
          : "")
        + ` — ${fait.avertissements_non_leves} avertissement(s) non levé(s).`);
    }
  }

  // **Le flux entre dans une file ; le rythme la vide.** Les événements
  // arrivent d'un coup — les rythmer est un choix de lecture, tenu ici.
  useEffect(() => {
    const source = new EventSource(
      `/api/v1/affaires/${affaireId}/memoire/releves/reconstruction`
      + `?chemin=${encodeURIComponent(cheminReleve)}`);
    source.onmessage = (message) => {
      file.current.push(JSON.parse(message.data));
    };
    source.addEventListener("fin", () => {
      finVue.current = true;
      source.close();
    });
    source.addEventListener("echec", (message) => {
      const { motif } = JSON.parse((message as MessageEvent).data);
      poserPhase({ nom: "echec", motif: String(motif) });
      source.close();
    });
    source.onerror = () => {
      // Une coupure avant `fin` : la machine le dit, plutôt qu'un écran figé.
      if (!finVue.current) {
        poserPhase((avant) => avant.nom === "termine" || avant.nom === "echec"
          ? avant : { nom: "echec", motif: "Le flux s'est interrompu." });
      }
      source.close();
    };
    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affaireId, cheminReleve]);

  useEffect(() => {
    const cadence = window.setInterval(() => {
      if (rythme === "pause") return;
      poserPhase((actuelle) => {
        if (actuelle.nom === "echec" || actuelle.nom === "termine") {
          return actuelle;
        }
        let budget = rythme === "direct" ? file.current.length : 1;
        while (budget > 0 && file.current.length > 0) {
          appliquer(file.current.shift()!);
          budget -= 1;
        }
        if (file.current.length === 0 && finVue.current) {
          return { nom: "termine" };
        }
        return actuelle;
      });
    }, rythme === "direct" ? 40 : 300);
    return () => window.clearInterval(cadence);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rythme]);

  useEffect(() => {
    bordRef.current?.scrollTo({ top: bordRef.current.scrollHeight });
  }, [bord.length]);

  useEffect(() => {
    const surTouche = (touche: KeyboardEvent) => {
      if (touche.key !== "Escape") return;
      // Échap sort d'abord du mode nommage — l'atelier ne se ferme que
      // depuis sa vue normale : perdre les deux d'un coup surprendrait.
      if (nommage) {
        poserNommage(null);
        poserPieceVive(null);
      } else {
        fermer();
      }
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [fermer, nommage]);

  // **Le cadrage d'ensemble, fixe.** Le viewBox reste calé sur tout le plan ;
  // le zoom et le déplacement s'appliquent à un `<g>` par-dessus (`xf`), ce qui
  // rend le zoom ancré au curseur une simple formule plutôt qu'un calcul de
  // lettrage `meet`.
  const base = useMemo(() => {
    if (!cadre) return { x: 0, y: 0, w: 1, h: 1 };
    const [x0, y0, x1, y1] = cadre.bornes;
    const marge = Math.max(x1 - x0, y1 - y0) * 0.05 || 1;
    return {
      x: x0 - marge, y: -(y1 + marge),
      w: x1 - x0 + 2 * marge, h: y1 - y0 + 2 * marge,
    };
  }, [cadre]);
  const vue = `${base.x} ${base.y} ${base.w} ${base.h}`;

  const [xf, poserXf] = useState({ k: 1, tx: 0, ty: 0 });
  const pointeurs = useRef<Map<number, { x: number; y: number }>>(new Map());
  const ecart = useRef<number | null>(null);  // distance du dernier pincement
  const bouge = useRef(false);  // un vrai déplacement a eu lieu → pas un clic

  // Un plan neuf se recadre. `xf` identité = cadrage d'ensemble.
  useEffect(() => { poserXf({ k: 1, tx: 0, ty: 0 }); }, [vue]);

  // Le point sous le curseur, en coordonnées du viewBox (échelle `meet` du
  // cadrage fixe — donc constante, contrairement à un viewBox mouvant).
  const versVue = (clientX: number, clientY: number) => {
    const svg = planRef.current;
    if (!svg) return { px: 0, py: 0, s0: 1 };
    const r = svg.getBoundingClientRect();
    const s0 = Math.min(r.width / base.w, r.height / base.h) || 1;
    const offX = (r.width - base.w * s0) / 2;
    const offY = (r.height - base.h * s0) / 2;
    return {
      px: base.x + (clientX - r.left - offX) / s0,
      py: base.y + (clientY - r.top - offY) / s0,
      s0,
    };
  };

  // Zoom ancré : le point sous le curseur reste sous le curseur.
  const zoomer = (clientX: number, clientY: number, facteur: number) => {
    const { px, py } = versVue(clientX, clientY);
    poserXf((v) => {
      const k = Math.min(Math.max(v.k * facteur, 1), 40);
      const g = k / v.k;
      return { k, tx: px - (px - v.tx) * g, ty: py - (py - v.ty) * g };
    });
  };

  // La molette ne peut pas être empêchée depuis le onWheel de React (écouteur
  // passif au niveau racine) : on l'attache nous-mêmes, non passif.
  useEffect(() => {
    const svg = planRef.current;
    if (!svg) return undefined;
    const surMolette = (e: WheelEvent) => {
      e.preventDefault();
      zoomer(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    svg.addEventListener("wheel", surMolette, { passive: false });
    return () => svg.removeEventListener("wheel", surMolette);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.w, base.h, base.x, base.y]);

  const surPointerDown = (e: React.PointerEvent) => {
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    bouge.current = false;
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };
  const surPointerMove = (e: React.PointerEvent) => {
    const prec = pointeurs.current.get(e.pointerId);
    if (!prec) return;
    pointeurs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const points = [...pointeurs.current.values()];
    const a = points[0];
    const b = points[1];
    if (a && b) {
      // Pincement : le rapport des distances zoome autour du milieu.
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (ecart.current) {
        zoomer((a.x + b.x) / 2, (a.y + b.y) / 2, dist / ecart.current);
      }
      ecart.current = dist;
      bouge.current = true;
      return;
    }
    const dx = e.clientX - prec.x;
    const dy = e.clientY - prec.y;
    if (Math.abs(dx) + Math.abs(dy) > 2) bouge.current = true;
    const { s0 } = versVue(e.clientX, e.clientY);
    poserXf((v) => ({ ...v, tx: v.tx + dx / s0, ty: v.ty + dy / s0 }));
  };
  const surPointerUp = (e: React.PointerEvent) => {
    pointeurs.current.delete(e.pointerId);
    if (pointeurs.current.size < 2) ecart.current = null;
    // Le drapeau tient le temps du `click` qui suit le `pointerup` (même tâche),
    // pour que la garde du local le voie ; on le lève juste après.
    if (bouge.current) window.setTimeout(() => { bouge.current = false; }, 0);
  };
  const transforme = xf.k !== 1 || xf.tx !== 0 || xf.ty !== 0;

  const dessines = useMemo(() => locaux.map((local) => {
    const rings = anneaux(local.wkt);
    const pieces = (local.pieces ?? []).map((piece, rangPiece) => ({
      piece,
      rangPiece,
      d: piece.wkt ? chemin(anneaux(piece.wkt)) : "",
    }));
    return {
      local,
      d: chemin(rings),
      centre: centre(rings),
      pieces,
      dPieces: pieces.filter((p) => p.d).map((p) => p.d).join(" "),
    };
  }), [locaux]);

  const inspecte = epingle != null
    ? locaux.find((l) => l.rang === epingle)
    : survole != null
      ? locaux.find((l) => l.rang === survole)
      : locaux.at(-1);

  async function annoter(cible: LocalReconstruit) {
    if (!formulaire || !formulaire.valeur.trim()) return;
    poserErreurAnnotation("");
    poserEnvoiAnnotation(true);
    try {
      const corps = await api.ecrire<{
        donnees: { question: string; reponse: string };
      }>(`/api/v1/affaires/${affaireId}/memoire/annotations`, {
        local: designation(cible),
        genre: formulaire.genre,
        valeur: formulaire.valeur.trim(),
      });
      const posee = {
        question: corps.donnees.question,
        reponse: corps.donnees.reponse,
        origine: "portail",
      };
      // La fiche la montre tout de suite — et la même annotation reposée
      // remplace l'ancienne, comme au journal.
      poserLocaux((avant) => avant.map((local) =>
        local.rang === cible.rang
          ? {
              ...local,
              decisions: [
                ...local.decisions.filter(
                  (d) => d.question !== posee.question),
                posee,
              ],
            }
          : local));
      noter(`✎ ${designation(cible)} — ${posee.question} → ${posee.reponse}.`
        + " Les missions suivantes l'appliqueront.");
      poserFormulaire(null);
    } catch (exc) {
      poserErreurAnnotation(exc instanceof ErreurApi ? exc.message
                            : "L'annotation n'a pas été enregistrée.");
    } finally {
      poserEnvoiAnnotation(false);
    }
  }

  async function ouvrirLeNommage() {
    poserErreurNommage("");
    poserNommage({ nom: "liste" });
    try {
      const corps = await api.lire<{
        donnees: { decisions?: {
          local?: string; question?: string; reponse?: string;
        }[] };
      }>(`/api/v1/affaires/${affaireId}/memoire`);
      const noms: Record<string, string> = {};
      for (const decision of corps.donnees.decisions ?? []) {
        if (decision.question === QUESTION_DU_NOM
            && decision.local && decision.reponse) {
          noms[decision.local] = decision.reponse;
        }
      }
      poserNomsPieces(noms);
    } catch {
      // La liste vit sans le registre ; les validations la rempliront.
    }
  }

  async function demanderConseil(cible: LocalReconstruit, rang: number) {
    const piece = cible.pieces?.[rang];
    if (!piece?.aire_m2) return;
    poserErreurNommage("");
    poserNommage({ nom: "conseil", piece: rang });
    try {
      const corps = await api.ecrire<{
        donnees: { nom: string; justification: string; deja: string | null;
                   sources: { origine: string; texte: string }[] };
      }>(`/api/v1/affaires/${affaireId}/memoire/nommages/proposition`, {
        releve: cheminReleve,
        ensemble: cible.nom,
        aire_m2: piece.aire_m2,
      });
      const conseil = corps.donnees;
      poserNommage({
        nom: "proposition", piece: rang,
        valeur: conseil.nom || "",
        justification: conseil.justification,
        sources: conseil.sources ?? [],
        deja: conseil.deja,
      });
    } catch (exc) {
      poserErreurNommage(exc instanceof ErreurApi ? exc.message
                         : "L'agent n'a pas pu être consulté.");
      poserNommage({ nom: "choix", piece: rang });
    }
  }

  async function validerLeNom(cible: LocalReconstruit, rang: number,
                              valeur: string) {
    const piece = cible.pieces?.[rang];
    const nom = valeur.trim();
    if (!piece || !nom) return;
    const designation = designationDePiece(piece);
    poserErreurNommage("");
    poserNommage({ nom: "envoi", piece: rang, valeur: nom });
    try {
      await api.ecrire(
        `/api/v1/affaires/${affaireId}/memoire/annotations`,
        { local: designation, genre: "nom", valeur: nom });
      poserNomsPieces((avant) => ({ ...avant, [designation]: nom }));
      noter(`✎ ${cible.nom} — ${designation} nommée « ${nom} ».`
        + " Les missions suivantes l'appliqueront.");
      poserNommage({ nom: "liste" });
    } catch (exc) {
      poserErreurNommage(exc instanceof ErreurApi ? exc.message
                         : "Le nom n'a pas été enregistré.");
      poserNommage({ nom: "manuel", piece: rang, valeur: nom });
    }
  }

  const rangCourant = RANG_DES_PHASES[phase.nom] ?? -1;
  const enCours = phase.nom !== "termine" && phase.nom !== "echec";

  return (
    <div className="atelier" role="dialog" aria-modal="true"
         aria-label={`Reconstruction de ${nom}`}>
      <div className="atelier__cadre">
        <div className="atelier__tete">
          <strong className="tronque">{nom}</strong>
          {entete && (
            <span className="discret tronque atelier__source">
              {entete.source} · {entete.unite} ({entete.unite_source})
            </span>
          )}
          <div className="atelier__phases" aria-label="Phases du rejeu">
            {PHASES.map((etape, rang) => (
              <span key={etape.clef}
                    className={`atelier__phase${
                      rang < rangCourant || !enCours
                        ? " atelier__phase--faite" : ""}${
                      rang === rangCourant && enCours
                        ? " atelier__phase--vive" : ""}`}>
                {rang === rangCourant && enCours && (
                  <span className="chat__pouls" aria-hidden="true" />
                )}
                {etape.libelle}
                {etape.clef === "construction" && phase.nom === "construction"
                  && ` ${phase.pas}/${phase.sur}`}
              </span>
            ))}
            {phase.nom === "termine" && (
              <span className="atelier__phase atelier__phase--terminee">
                Terminé
              </span>
            )}
            {phase.nom === "echec" && (
              <span className="atelier__phase atelier__phase--echec">
                Échec
              </span>
            )}
          </div>
          {enCours && rythme !== "direct" && (
            <button type="button" className="bouton"
                    onClick={() => poserRythme(
                      rythme === "pause" ? "progressif" : "pause")}>
              {rythme === "pause" ? "Reprendre" : "Pause"}
            </button>
          )}
          {enCours && rythme !== "direct" && (
            <button type="button" className="bouton"
                    onClick={() => poserRythme("direct")}>
              Tout dérouler
            </button>
          )}
          <button type="button" className="chat__voile-fermer"
                  aria-label="Fermer l'atelier" title="Fermer (Échap)"
                  onClick={fermer}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div className="atelier__corps">
          <div className="atelier__scene">
            {phase.nom === "echec" ? (
              <div className="atelier__echec">
                <strong>Le rejeu n'a pas abouti.</strong>
                <p>{phase.motif}</p>
              </div>
            ) : (
              <svg ref={planRef}
                   className={`atelier__plan${
                     nommage ? " atelier__plan--nommage" : ""}`}
                   viewBox={vue} preserveAspectRatio="xMidYMid meet"
                   style={{ touchAction: "none",
                            cursor: transforme ? "grab" : undefined }}
                   onPointerDown={surPointerDown}
                   onPointerMove={surPointerMove}
                   onPointerUp={surPointerUp}
                   onPointerCancel={surPointerUp}>
                <g transform={`translate(${xf.tx} ${xf.ty}) scale(${xf.k})`}>
                {dessines.map(({ local, d, dPieces, pieces,
                                 centre: [cx, cy] }) => (
                  <g key={local.rang}
                     className={`atelier__local atelier__local--${local.nommage}${
                       local.multi ? " atelier__local--multi" : ""}${
                       local.source === "contour inféré"
                         ? " atelier__local--infere" : ""}${
                       local.rang === survole ? " atelier__local--vif" : ""}${
                       local.rang === epingle
                         ? " atelier__local--epingle" : ""}`}
                     onMouseEnter={() => { if (!nommage) poserSurvole(local.rang); }}
                     onMouseLeave={() => { if (!nommage) poserSurvole(null); }}
                     onClick={() => {
                       // Un déplacement du plan ne doit pas épingler le local
                       // qui se trouvait sous le doigt à la fin du geste.
                       if (nommage || bouge.current) return;
                       poserEpingle(
                         epingle === local.rang ? null : local.rang);
                       poserFormulaire(null);
                       poserErreurAnnotation("");
                     }}>
                    <path d={d} fillRule="evenodd"
                          vectorEffect="non-scaling-stroke" />
                    {nommage && local.rang === epingle ? (
                      // Le mode nommage : chaque pièce devient une cible —
                      // la liste et la scène se montrent l'une l'autre.
                      pieces.filter((p) => p.d).map(({ piece, rangPiece, d: dP }) => (
                        <path key={rangPiece}
                              className={`atelier__piece${
                                pieceVive === rangPiece
                                  ? " atelier__piece--vive" : ""}`}
                              d={dP} fillRule="evenodd"
                              vectorEffect="non-scaling-stroke"
                              aria-label={designationDePiece(piece)}
                              onMouseEnter={() => poserPieceVive(rangPiece)}
                              onMouseLeave={() => poserPieceVive(null)}
                              onClick={() => {
                                poserErreurNommage("");
                                poserNommage({ nom: "choix",
                                               piece: rangPiece });
                              }} />
                      ))
                    ) : dPieces ? (
                      <path className="atelier__pieces" d={dPieces}
                            fillRule="evenodd"
                            vectorEffect="non-scaling-stroke" />
                    ) : null}
                    {local.nom && local.aire_m2 > 6 && (
                      <text x={cx} y={cy} textAnchor="middle">
                        {local.nom}
                      </text>
                    )}
                  </g>
                ))}
                </g>
              </svg>
            )}
            {phase.nom !== "echec" && transforme && (
              <button type="button" className="bouton atelier__recadrer"
                      onClick={() => poserXf({ k: 1, tx: 0, ty: 0 })}>
                Recadrer
              </button>
            )}
            <div className="atelier__legende">
              <span><i className="atelier__teinte atelier__teinte--deterministe" />Déterministe</span>
              <span><i className="atelier__teinte atelier__teinte--inference" />Inférence</span>
              <span><i className="atelier__teinte atelier__teinte--anonyme" />Anonyme</span>
              <span><i className="atelier__teinte atelier__teinte--multi" />Ensemble multi-locaux</span>
            </div>
          </div>

          <aside className="atelier__flanc">
            {nommage && inspecte && epingle === inspecte.rang ? (
              <div className="atelier__fiche atelier__nommage">
                <div className="atelier__fiche-tete">
                  <strong className="tronque">
                    Nommer les pièces — {inspecte.nom}
                  </strong>
                  <button type="button" className="lien-bouton"
                          onClick={() => {
                            poserNommage(null);
                            poserPieceVive(null);
                          }}>
                    Terminer
                  </button>
                </div>
                <p className="discret">
                  Le reste du plan est en retrait. Survolez une pièce pour la
                  situer, cliquez pour la nommer — Échap revient à la fiche.
                </p>
                <div className="atelier__nommage-liste" role="list">
                  {(inspecte.pieces ?? []).map((piece, rangPiece) => {
                    const designation = designationDePiece(piece);
                    const valide = nomsPieces[designation];
                    const active = nommage.nom !== "liste"
                      && nommage.piece === rangPiece;
                    return (
                      <div key={designation} role="listitem"
                           className={`atelier__nommage-piece${
                             pieceVive === rangPiece
                               ? " atelier__nommage-piece--vive" : ""}${
                             active ? " atelier__nommage-piece--active" : ""}`}
                           onMouseEnter={() => poserPieceVive(rangPiece)}
                           onMouseLeave={() => poserPieceVive(null)}>
                        <button type="button"
                                className="atelier__nommage-entete"
                                onClick={() => {
                                  poserErreurNommage("");
                                  poserNommage({ nom: "choix",
                                                 piece: rangPiece });
                                }}>
                          <span className="tronque">
                            {valide ?? (piece.nom || "(sans nom)")}
                          </span>
                          <span className="discret">
                            {surfaceTexte(piece.aire_m2)}
                          </span>
                          {valide ? (
                            <span className="etiquette atelier__badge--deterministe">
                              métreur
                            </span>
                          ) : piece.nom ? (
                            <span className="etiquette">relevé</span>
                          ) : (
                            <span className="etiquette atelier__badge--anonyme">
                              à nommer
                            </span>
                          )}
                        </button>
                        {active && nommage.nom === "choix" && (
                          <div className="rangee atelier__nommage-choix">
                            <button type="button" className="bouton"
                                    onClick={() => poserNommage({
                                      nom: "manuel", piece: rangPiece,
                                      valeur: valide ?? piece.nom ?? "",
                                    })}>
                              Nommer à la main
                            </button>
                            <button type="button" className="bouton"
                                    onClick={() => void demanderConseil(
                                      inspecte, rangPiece)}>
                              Demander à l'agent
                            </button>
                            <button type="button"
                                    className="bouton bouton--primaire"
                                    onClick={() => poserChiffrage({
                                      nom: valide ?? designation,
                                      aire: piece.aire_m2,
                                      perimetre: piece.perimetre_m ?? null,
                                    })}>
                              Chiffrer cette pièce
                            </button>
                          </div>
                        )}
                        {active && nommage.nom === "manuel" && (
                          <div className="atelier__nommage-saisie">
                            <input value={nommage.valeur}
                                   placeholder="Séjour"
                                   aria-label={`Nom de ${designation}`}
                                   autoFocus
                                   onChange={(e) => poserNommage({
                                     ...nommage, valeur: e.target.value })} />
                            <div className="rangee">
                              <button type="button"
                                      className="bouton bouton--primaire"
                                      disabled={!nommage.valeur.trim()}
                                      onClick={() => void validerLeNom(
                                        inspecte, rangPiece, nommage.valeur)}>
                                Valider ce nom
                              </button>
                              <button type="button" className="bouton"
                                      onClick={() => poserNommage(
                                        { nom: "liste" })}>
                                Annuler
                              </button>
                            </div>
                          </div>
                        )}
                        {active && nommage.nom === "conseil" && (
                          <p className="discret atelier__nommage-attente">
                            L'agent consulte le registre, le relevé, le CCTP
                            et le plan PDF…
                          </p>
                        )}
                        {active && nommage.nom === "proposition" && (
                          <div className="atelier__nommage-saisie">
                            {nommage.deja && (
                              <p className="discret">
                                Déjà tranchée au registre —{" "}
                                <strong>{nommage.deja}</strong>. Valider à
                                nouveau remplace.
                              </p>
                            )}
                            <input value={nommage.valeur}
                                   placeholder="(l'agent n'a pas conclu)"
                                   aria-label={`Nom proposé pour ${designation}`}
                                   onChange={(e) => poserNommage({
                                     ...nommage, valeur: e.target.value })} />
                            {nommage.justification && (
                              <p className="atelier__nommage-justification">
                                {nommage.justification}
                              </p>
                            )}
                            {nommage.sources.length > 0 && (
                              <details className="atelier__nommage-preuves">
                                <summary>
                                  {nommage.sources.length} preuve(s) réunie(s)
                                </summary>
                                {nommage.sources.map((source, i) => (
                                  <p key={i}>
                                    <span className="etiquette">
                                      {source.origine}
                                    </span>{" "}
                                    {source.texte}
                                  </p>
                                ))}
                              </details>
                            )}
                            <div className="rangee">
                              <button type="button"
                                      className="bouton bouton--primaire"
                                      disabled={!nommage.valeur.trim()}
                                      onClick={() => void validerLeNom(
                                        inspecte, rangPiece, nommage.valeur)}>
                                Valider ce nom
                              </button>
                              <button type="button" className="bouton"
                                      onClick={() => poserNommage(
                                        { nom: "liste" })}>
                                Annuler
                              </button>
                            </div>
                          </div>
                        )}
                        {active && nommage.nom === "envoi" && (
                          <p className="discret atelier__nommage-attente">
                            Enregistrement de « {nommage.valeur} »…
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {erreurNommage && (
                  <div className="avis avis--erreur">{erreurNommage}</div>
                )}
                <p className="discret atelier__promesse">
                  Un nom validé s'inscrit aux décisions du métreur : les
                  missions suivantes l'appliqueront, et l'onglet Mémoire
                  l'administre.
                </p>
              </div>
            ) : inspecte ? (
              <div className="atelier__fiche">
                <div className="atelier__fiche-tete">
                  <strong>{inspecte.nom || "(sans nom)"}</strong>
                  <span className={`etiquette atelier__badge--${inspecte.nommage}`}>
                    {inspecte.nommage === "deterministe" ? "déterministe"
                      : inspecte.nommage === "inference" ? "inférence"
                        : "anonyme"}
                  </span>
                  {epingle === inspecte.rang && (
                    <button type="button" className="lien-bouton"
                            onClick={() => {
                              poserEpingle(null);
                              poserFormulaire(null);
                            }}>
                      Libérer
                    </button>
                  )}
                </div>
                <dl className="atelier__faits">
                  <dt>Pourquoi ce nom</dt>
                  <dd>
                    {LIBELLES_NOMMAGE[inspecte.nommage]}
                    {inspecte.noms_dedans.length > 1 && (
                      <> — étiquettes dans le contour :{" "}
                        {inspecte.noms_dedans.join(", ")}</>
                    )}
                  </dd>
                  <dt>Source</dt>
                  <dd className="mono">
                    {inspecte.source}
                    {inspecte.calque && ` · calque ${inspecte.calque}`}
                  </dd>
                  <dt>Surfaces</dt>
                  <dd>
                    relevée {surfaceTexte(inspecte.aire_m2)}
                    {inspecte.aire_declaree_m2 != null
                      && ` · écrite au plan ${surfaceTexte(inspecte.aire_declaree_m2)}`}
                    {inspecte.aire_recalculee_m2 != null && (
                      <> · recalculée {surfaceTexte(inspecte.aire_recalculee_m2)}
                        {inspecte.aire_verifiee
                          ? <span className="atelier__ok"> ✓ vérifiée</span>
                          : <span className="atelier__ko"> ✗ ne retombe pas</span>}
                      </>
                    )}
                  </dd>
                  {(inspecte.pieces?.length ?? 0) > 0 && (
                    <>
                      <dt>Composition de l'ensemble</dt>
                      <dd>
                        {inspecte.pieces!.map((piece) => (
                          <p key={`${piece.nom}-${piece.aire_m2}`}>
                            {piece.nom || "(sans nom)"} —{" "}
                            {surfaceTexte(piece.aire_m2)}
                            {piece.cloison_manquante && (
                              <span className="atelier__doute">
                                {" "}· cloison manquante
                              </span>
                            )}
                          </p>
                        ))}
                        {inspecte.cloisons_m2 != null && (
                          <p className="discret">
                            cloisons : {surfaceTexte(inspecte.cloisons_m2)}
                          </p>
                        )}
                      </dd>
                    </>
                  )}
                  {(inspecte.hauteurs_etiquetees?.length ?? 0) > 0 && (
                    <>
                      <dt>Hauteurs écrites au plan</dt>
                      <dd>
                        {[...new Set(inspecte.hauteurs_etiquetees!.map(
                          (h) => `${h.genre} ${h.valeur_m} m`))].join(" · ")}
                      </dd>
                    </>
                  )}
                  {(inspecte.sous_zones?.length ?? 0) > 0 && (
                    <>
                      <dt>Sols relevés</dt>
                      <dd>
                        {inspecte.sous_zones!.map((zone) => (
                          <p key={`${zone.materiau}-${zone.aire_m2}`}>
                            {zone.materiau.toLowerCase()} —{" "}
                            {surfaceTexte(zone.aire_m2)}
                            {zone.etiquette && (
                              <> · coin « {zone.etiquette} »</>
                            )}
                          </p>
                        ))}
                      </dd>
                    </>
                  )}
                  {inspecte.incertitudes.length > 0 && (
                    <>
                      <dt>Incertitudes</dt>
                      <dd>
                        {inspecte.incertitudes.map((texte) => (
                          <p key={texte} className="atelier__doute">{texte}</p>
                        ))}
                      </dd>
                    </>
                  )}
                  {inspecte.avertissements.length > 0 && (
                    <>
                      <dt>Avertissements du journal</dt>
                      <dd>
                        {inspecte.avertissements.map((averti) => (
                          <p key={averti.texte}
                             className={averti.leve ? "atelier__leve"
                               : "atelier__doute"}>
                            {averti.leve ? "✓ levé — " : "⚠ "}
                            {averti.texte}
                          </p>
                        ))}
                      </dd>
                    </>
                  )}
                  {inspecte.decisions.length > 0 && (
                    <>
                      <dt>Décisions du métreur</dt>
                      <dd>
                        {inspecte.decisions.map((decision) => (
                          <p key={decision.question}>
                            {decision.question}
                            {" "}<strong>→ {decision.reponse}</strong>
                          </p>
                        ))}
                      </dd>
                    </>
                  )}
                </dl>
                {epingle === inspecte.rang && !formulaire && (
                  <div className="rangee atelier__gestes">
                    <button type="button" className="bouton"
                            onClick={() => {
                              poserErreurAnnotation("");
                              poserFormulaire({ genre: "nom", valeur: "" });
                            }}>
                      <svg width="15" height="15" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" strokeWidth="2.2"
                           aria-hidden="true">
                        <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                      </svg>
                      Annoter ce local
                    </button>
                    {(inspecte.pieces?.length ?? 0) > 0 && (
                      <button type="button" className="bouton"
                              onClick={() => void ouvrirLeNommage()}>
                        <svg width="15" height="15" viewBox="0 0 24 24"
                             fill="none" stroke="currentColor"
                             strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="8" height="8" rx="1" />
                          <rect x="13" y="3" width="8" height="8" rx="1" />
                          <rect x="3" y="13" width="8" height="8" rx="1" />
                          <path d="M17 14v6M14 17h6" />
                        </svg>
                        Nommer les pièces
                      </button>
                    )}
                    <button type="button" className="bouton bouton--primaire"
                            onClick={() => poserChiffrage({
                              nom: designation(inspecte),
                              ...metreChiffrage(inspecte),
                            })}>
                      <svg width="15" height="15" viewBox="0 0 24 24"
                           fill="none" stroke="currentColor" strokeWidth="2"
                           aria-hidden="true">
                        <rect x="4" y="2" width="16" height="20" rx="2" />
                        <path d="M8 6h8M8 11h2m4 0h2M8 15h2m4 0h2M8 19h2m4 0h2" />
                      </svg>
                      Chiffrer ce local
                    </button>
                  </div>
                )}
                {epingle === inspecte.rang && formulaire && (
                  <div className="atelier__formulaire">
                    <div className="champ">
                      <label htmlFor="atelier-genre">Ce que vous posez</label>
                      <select id="atelier-genre" value={formulaire.genre}
                              onChange={(e) => poserFormulaire(
                                { ...formulaire, genre: e.target.value })}>
                        {GENRES_ANNOTATION.map((genre) => (
                          <option key={genre.clef} value={genre.clef}>
                            {genre.libelle}
                            {genre.unite ? ` (${genre.unite})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="champ">
                      <label htmlFor="atelier-valeur">Valeur</label>
                      <div className="atelier__valeur">
                        {formulaire.genre === "commentaire" ? (
                          <textarea id="atelier-valeur" rows={3}
                                    value={formulaire.valeur}
                                    onChange={(e) => poserFormulaire(
                                      { ...formulaire,
                                        valeur: e.target.value })} />
                        ) : (
                          <input id="atelier-valeur" value={formulaire.valeur}
                                 placeholder={GENRES_ANNOTATION.find(
                                   (g) => g.clef === formulaire.genre)?.unite
                                   ? "2,50" : "Pièce de vie"}
                                 onChange={(e) => poserFormulaire(
                                   { ...formulaire,
                                     valeur: e.target.value })} />
                        )}
                        {(() => {
                          const unite = GENRES_ANNOTATION.find(
                            (g) => g.clef === formulaire.genre)?.unite;
                          return unite
                            ? <span className="discret">{unite}</span> : null;
                        })()}
                      </div>
                    </div>
                    {erreurAnnotation && (
                      <div className="avis avis--erreur">{erreurAnnotation}</div>
                    )}
                    <div className="rangee">
                      <button type="button"
                              className="bouton bouton--primaire"
                              disabled={envoiAnnotation
                                || !formulaire.valeur.trim()}
                              onClick={() => void annoter(inspecte)}>
                        Enregistrer
                      </button>
                      <button type="button" className="bouton"
                              onClick={() => poserFormulaire(null)}>
                        Annuler
                      </button>
                    </div>
                    <p className="discret atelier__promesse">
                      L'annotation s'inscrit aux décisions du métreur : les
                      missions suivantes l'appliqueront, et l'onglet Mémoire
                      l'administre.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="atelier__fiche">
                <p className="discret">
                  Survolez un contour pour lire sa provenance, cliquez pour
                  l'épingler — ou choisissez un local ci-dessous.
                </p>
                {/* **L'index des locaux, au clavier.** Le plan est en SVG et ne
                    se sélectionne qu'à la souris ; cette liste de boutons donne
                    à chaque local un point de tabulation nommé, avec la même
                    surbrillance croisée que le survol du contour. */}
                <div className="atelier__index">
                  {locaux.map((local) => (
                    <button key={local.rang} type="button"
                            className={`atelier__index-item atelier__index-item--${local.nommage}`}
                            onMouseEnter={() => poserSurvole(local.rang)}
                            onMouseLeave={() => poserSurvole(null)}
                            onFocus={() => poserSurvole(local.rang)}
                            onBlur={() => poserSurvole(null)}
                            onClick={() => {
                              poserEpingle(local.rang);
                              poserFormulaire(null);
                              poserErreurAnnotation("");
                            }}>
                      <span className="tronque">{designation(local)}</span>
                      {local.multi && (
                        <span className="etiquette">ensemble</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bilan && (
              <div className="atelier__bilan">
                <span className="etiquette">
                  {bilan.comptes.deterministe} déterministe(s)
                </span>
                <span className="etiquette">
                  {bilan.comptes.inference} par inférence
                </span>
                <span className="etiquette">
                  {bilan.comptes.anonyme} anonyme(s)
                </span>
                <span className="etiquette">
                  {bilan.comptes.multi} ensemble(s)
                </span>
                <span className={bilan.avertissements_non_leves
                  ? "etiquette atelier__badge--inference" : "etiquette"}>
                  {bilan.avertissements_non_leves} avertissement(s) non levé(s)
                </span>
              </div>
            )}

            {chevauchements.length > 0 && (
              <div className="atelier__chevauchements">
                <strong>Contours en dispute</strong>
                {chevauchements.map((c) => (
                  <p key={`${c.a}-${c.b}`} className="atelier__doute">
                    « {c.a} » et « {c.b} » se recouvrent de {c.aire_m2} m².
                  </p>
                ))}
              </div>
            )}
            <div className="atelier__bord" ref={bordRef}
                 aria-label="Journal de l'atelier" aria-live="polite">
              {bord.map((ligne, rang) => (
                <div key={rang} className="atelier__ligne">{ligne}</div>
              ))}
            </div>
          </aside>
        </div>
        {chiffrage && (
          <Chiffrage
            affaireId={affaireId}
            local={chiffrage.nom}
            aire={chiffrage.aire}
            perimetre={chiffrage.perimetre}
            releve={cheminReleve}
            fermer={() => poserChiffrage(null)}
          />
        )}
      </div>
    </div>
  );
}
